import React, {ReactElement, useEffect, useState} from 'react';
import {useSetModalContent} from '@fo/shared-providers/ModalContextProvider';
import LoginModalInTwoSteps from '@fo/components/Identity/LoginModalInTwoSteps';
import CreateAccountTypeModal from '@fo/components/Identity/CreateAccountTypeModal/CreateAccountTypeModal';
import NonExistentUserModal from '@fo/components/Identity/LoginModalInTwoSteps/nonExistentUserModal';
import ForgotPasswordModal from '@fo/components/Identity/ForgotPasswordModal/ForgotPasswordModal';
import {segmentEvents, track} from '@fo/shared-analytics/tracking/segment';

import useIsRedirectFromBRS from './useIsRedirectFromBRS';

type IdentityModalType = 'LOGIN' | 'CREATE_ACCOUNT_TYPE' | 'FORGOT_PASSWORD' | 'NON_EXISTING_USER';

type IdentityModalMethods = {
  showLoginModal: () => void;
  showCreateAccountModal: () => void;
  showForgotPasswordModal: () => void;
  showNonExistentUserModal: () => void;
};

const useIdentityModal = (): IdentityModalMethods => {
  const showModal = useSetModalContent();

  const [activeModalType, setActiveModalType] = useState<IdentityModalType>('LOGIN');
  const [modalVisible, setModalVisible] = useState<boolean>(false);

  const showLoginModal = () => {
    setActiveModalType('LOGIN');
    setModalVisible(true);
  };
  const showCreateAccountModal = () => {
    setActiveModalType('CREATE_ACCOUNT_TYPE');
    setModalVisible(true);
    track(segmentEvents.actions.modalCreateAccountShown);
  };
  const showForgotPasswordModal = () => {
    setActiveModalType('FORGOT_PASSWORD');
    setModalVisible(true);
  };
  const showNonExistentUserModal = () => {
    setActiveModalType('NON_EXISTING_USER');
    setModalVisible(true);
  };

  const onModalClose = () => {
    setModalVisible(false);
    showModal(undefined);
  };

  const isRedirectFromBRS = useIsRedirectFromBRS();

  const loginModal = (
    <LoginModalInTwoSteps
      showCreateAccount={showCreateAccountModal}
      showForgotPassword={showForgotPasswordModal}
      onModalClose={onModalClose}
      showNonExistentUserModal={showNonExistentUserModal}
      withBRSRedirect={isRedirectFromBRS}
    />
  );

  const nonExistentUserModal = (
    <NonExistentUserModal onModalClose={onModalClose} showCreateAccountModal={showCreateAccountModal} />
  );

  const createAccountTypeModal = <CreateAccountTypeModal showLogin={showLoginModal} onModalClose={onModalClose} />;
  const forgotPasswordModal = <ForgotPasswordModal onModalClose={onModalClose} />;

  let activeIdentityModal: ReactElement;

  switch (activeModalType) {
    case 'LOGIN':
      activeIdentityModal = loginModal;
      break;
    case 'CREATE_ACCOUNT_TYPE':
      activeIdentityModal = createAccountTypeModal;
      break;
    case 'FORGOT_PASSWORD':
      activeIdentityModal = forgotPasswordModal;
      break;
    case 'NON_EXISTING_USER':
      activeIdentityModal = nonExistentUserModal;
      break;
    default:
      activeIdentityModal = loginModal;
  }

  useEffect(() => {
    if (modalVisible) {
      showModal(activeIdentityModal);
    }
  }, [activeIdentityModal, modalVisible]);

  return {
    showLoginModal,
    showCreateAccountModal,
    showForgotPasswordModal,
    showNonExistentUserModal,
  };
};

export default useIdentityModal;
